import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LegalAccountingServices } from "@/components/LegalAccountingServices";
import { Button } from "@/components/ui/button";
import { Scale, Calculator, HelpCircle, Mail, MessageSquare, ShieldCheck } from "lucide-react";

export default function LegalAccountingPage() {
  return (
    <div className="min-h-screen bg-parchment">
      <Navbar />
      <main className="pt-20">
        {/* Hero de Asistencia */}
        <section className="py-24 bg-moss text-parchment border-b-2 border-ink">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <span className="text-xs font-black uppercase tracking-[0.5em] text-terracotta mb-6 block">Respaldo Profesional</span>
              <h1 className="text-6xl md:text-8xl font-display font-black uppercase italic leading-none mb-8">
                Asistencia <br /> <span className="text-ink">Legal y Contable</span>
              </h1>
              <p className="text-2xl font-serif italic opacity-60 leading-relaxed max-w-2xl mb-12">
                No camines solo en las complejidades del negocio inmobiliario. Acceso directo a expertos para resolver tus dudas legales y tributarias.
              </p>
              <Button variant="terracotta" size="lg" className="h-16 px-12 text-lg">SOLICITAR CONSULTA</Button>
            </div>
          </div>
        </section>

        {/* Ejemplos de Consultas */}
        <section className="py-24 bg-parchment">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div className="space-y-12">
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-terracotta flex items-center justify-center text-parchment">
                    <Scale className="h-8 w-8" />
                  </div>
                  <h2 className="text-4xl font-display font-black uppercase italic">Consultas Legales</h2>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  {[
                    "Garantías para cobro de honorarios.",
                    "Alzamientos de hipotecas y gravámenes.",
                    "Revisión de mandatos corredor-propietario.",
                    "Orientación en reparos de los CBR.",
                    "Revisión de contratos de canje.",
                    "Cobro de honorarios impagos.",
                    "Posesiones efectivas testadas e intestadas.",
                    "Consultas sobre cesiones de derecho."
                  ].map((text, i) => (
                    <div key={i} className="flex gap-4 p-6 brutalist-border-sm bg-ink/5 hover:bg-ink hover:text-parchment transition-all group">
                      <HelpCircle className="h-5 w-5 text-terracotta flex-shrink-0" />
                      <p className="font-serif italic font-medium">{text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-12">
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-ink flex items-center justify-center text-parchment">
                    <Calculator className="h-8 w-8" />
                  </div>
                  <h2 className="text-4xl font-display font-black uppercase italic">Consultas Contables</h2>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  {[
                    "Comprobantes en venta de propiedades.",
                    "Emisión de boletas de honorarios.",
                    "Tipos de facturas e iniciación de actividades.",
                    "IVA en comisiones inmobiliarias.",
                    "Tratamiento tributario en arriendos.",
                    "Boletas vs Facturas de servicios."
                  ].map((text, i) => (
                    <div key={i} className="flex gap-4 p-6 brutalist-border-sm bg-ink/5 hover:bg-ink hover:text-parchment transition-all group">
                      <HelpCircle className="h-5 w-5 text-terracotta flex-shrink-0" />
                      <p className="font-serif italic font-medium">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Consulta Específica */}
        <section className="py-24 bg-moss text-parchment relative overflow-hidden">
          <div className="absolute inset-0 opacity-5 grain-texture" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto p-12 brutalist-border border-terracotta bg-ink text-center">
              <h4 className="text-3xl font-display font-black uppercase italic mb-6">¿Tienes una consulta específica?</h4>
              <p className="text-xl font-serif italic text-parchment/60 mb-12">Nuestros expertos responden en menos de 24 horas hábiles.</p>
              <Button variant="terracotta" size="lg" className="h-16 px-16 text-xs font-black tracking-[0.2em]">
                ABRIR TICKET DE SOPORTE
              </Button>
            </div>
          </div>
        </section>

        {/* Cómo funciona el Soporte */}
        <section className="py-24 bg-ink text-parchment">
          <div className="container mx-auto px-4 text-center">
            <span className="text-xs font-black uppercase tracking-[0.5em] text-terracotta mb-8 block">Proceso de Respuesta</span>
            <h2 className="text-5xl md:text-7xl font-display font-black uppercase italic mb-16">¿Cómo trabajamos?</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
              {[
                { title: "Envía tu duda", desc: "Abre un ticket desde tu dashboard detallando tu consulta.", icon: Mail },
                { title: "Análisis experto", desc: "Nuestros abogados o contadores revisan tu caso en menos de 24h.", icon: ShieldCheck },
                { title: "Resolución", desc: "Recibes una respuesta detallada y clara vía correo o plataforma.", icon: MessageSquare }
              ].map((step, i) => (
                <div key={i} className="brutalist-border border-parchment/20 p-12 hover:bg-terracotta transition-colors group">
                  <step.icon className="h-12 w-12 text-terracotta group-hover:text-ink mb-8 mx-auto" />
                  <h3 className="text-2xl font-display font-black uppercase italic mb-4">{step.title}</h3>
                  <p className="font-serif italic opacity-60 group-hover:opacity-100">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
